import * as aws from "common/aws";
import * as hashes from "common/hashes";
import * as logger from "common/logger";
import * as config from "config";
import * as FileSaver from "file-saver";
import * as utils from "utils";

export const spreadsheetMimeTypes = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument.spreadsheet",
];
export const docMimeTypes = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.oasis.opendocument.text",
];
export const presMimeTypes = [
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.presentation",
];
export const legacyMicrosoft = [
  "application/vnd.ms-powerpoint",
  "application/msword",
  "application/vnd.ms-excel",
];
export const pdfMimeTypes = ["application/pdf"];
export const archMimeTypes = [
  "application/zip",
  "application/gzip",
  "application/x-tar",
];
export const tabularMimeTypes = [
  ...spreadsheetMimeTypes,
  "text/csv",
  "text/tsv",
  "text/psv",
];
export const rawViewableMimeTypes = [...pdfMimeTypes, "text/html"];
export const htmlViewableMimeTypes = [
  ...spreadsheetMimeTypes,
  ...docMimeTypes,
  ...presMimeTypes,
  ...legacyMicrosoft,
  "text/csv",
  "text/psv",
  "text/tsv",
  "text/plain",
  "text/base64",
  "text/xml",
];

export const encyptedMimeTypes = ["application/encrypted"];

const getUrl = (path) => config.endpoints.files(path);
export const getUploadUrlUrl = () => getUrl("get-upload-url");
export const getDownloadUrlUrl = (id) => getUrl(`get-download-url/${id}`);
export const notifyUrl = (id) => getUrl(`file-loaded/${id}`);
export const statusUrl = (id) => getUrl(`get-status/${id}`);
export const statusTreeUrl = (id) => getUrl(`get-status-tree/${id}`);
export const uploadFromS3 = () => getUrl("upload-from-s3");
export const cleanOfficeFileUrl = (id) => getUrl(`clean-office-file/${id}`);

const TEXT_STATUS_COMPLETE = [
  "TEXT_FILE",
  "NOT_AVAILABLE",
  "LOADED",
  "NOT_READABLE",
  "CONVERSION_FAILED",
  "NOT_VALID",
];
const TEXT_STATUS_SUCCESS = ["TEXT_FILE", "LOADED"];
const STATUS_COMPLETE = [
  "LOADED",
  "NOT_VALID",
  "NOT_AVAILABLE",
  "NOT_READABLE",
];
export const fileReady = async (
  id,
  waitText,
  impersonateCompanyId = undefined,
  expectedMimeTypes = null
) => {
  const url = statusUrl(id);
  let meta = {};

  const ready = () => {
    const loadComplete = STATUS_COMPLETE.includes(meta.status);
    if (
      loadComplete &&
      expectedMimeTypes &&
      !expectedMimeTypes.includes(meta.mimeType)
    )
      return true;
    if (!waitText || !loadComplete) return loadComplete;
    if (meta.status !== "LOADED") return true;
    return TEXT_STATUS_COMPLETE.includes(meta.textStatus);
  };
  while (!ready()) {
    await new Promise((res) => setTimeout(res, 500));
    const resp = await utils.authenticatedFetch(
      url,
      undefined,
      impersonateCompanyId
    );
    meta = await resp.json();
  }
  return [
    meta.status === "LOADED",
    !waitText || TEXT_STATUS_SUCCESS.includes(meta.textStatus),
    meta.mimeType,
  ];
};

export const fileStatus = async (id) => {
  const resp = await utils.authenticatedFetch(statusUrl(id));
  return await resp.json();
};

export const fileStatusTree = async (id) => {
  const resp = await utils.authenticatedFetch(statusTreeUrl(id));
  return await resp.json();
};

export const createFileEntry = async (
  file,
  waitText = false,
  expectedMimeTypes = null,
  impersonateCompanyId = undefined,
  returnMetadata = false
) => {
  const filename = file.name;
  const base64Sha256 = await hashes.hashFile(file, {
    algorithm: "SHA-256",
    format: "base64",
  });
  const body = {
    filename: filename,
    base64_sha256: base64Sha256,
  };
  const url = getUploadUrlUrl();
  try {
    const uploadResp = await utils.authenticatedFetch(
      url,
      {
        body: JSON.stringify(body),
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
      },
      impersonateCompanyId
    );
    const uploadSpec = await uploadResp.json();
    await aws.uploadFile(file, uploadSpec.postingSpec);
    const noResponseRequired = () => {};
    utils
      .authenticatedFetch(
        notifyUrl(uploadSpec.id),
        undefined,
        impersonateCompanyId
      )
      .then(noResponseRequired);
    const [load_ok, text_ok, mimeType] = await fileReady(
      uploadSpec.id,
      waitText,
      impersonateCompanyId,
      expectedMimeTypes
    );
    if (!load_ok) {
      throw Error("Failed to save file to Marmalade");
    } else if (expectedMimeTypes && !expectedMimeTypes.includes(mimeType)) {
      const message =
        mimeType === "inode/x-empty"
          ? "Uploaded file is empty, this might be a network or firewall issue"
          : `Unrecognised file type: '${mimeType}'`;
      logger.breadcrumb({ mimeType, message });
      throw Error(message);
    } else if (!text_ok) {
      throw Error("Conversion to text failed");
    }

    if (returnMetadata) {
      return {
        id: uploadSpec.id,
        hash: base64Sha256,
        size: file.size,
      };
    }
    return uploadSpec.id;
  } catch (error) {
    logger.exception(error, {
      breadcrumb: { file, base64Sha256, filename },
    });
    throw error;
  }
};

export const uploadJson = async (data, filename) => {
  const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
  const file = new File([blob], filename, { type: "application/json" });
  return createFileEntry(file, false, null);
};

export const downloadJson = async (fileId) => {
  const url = await getDownloadUrl(fileId);
  const response = await fetch(url);
  return response.json();
};

export const getDownloadUrl = async (id, impersonateCompanyId = undefined) => {
  const url = getDownloadUrlUrl(id);
  try {
    const resp = await utils.authenticatedFetch(
      url,
      undefined,
      impersonateCompanyId
    );
    const json = await resp.json();
    return await json.url;
  } catch (error) {
    logger.exception(error, {
      breadcrumb: { call: "getDownloadUrl", id },
    });
    throw error;
  }
};

export const saveFile = async (
  id,
  filename,
  impersonateCompanyId = undefined
) => {
  const url = await getDownloadUrl(id, impersonateCompanyId);
  const resp = await utils.plainFetch(url);
  const blob = await resp.blob();
  await FileSaver.saveAs(blob, filename);
};

export const uploadEmailFromFiles = async (listener, identifier, filename) => {
  const url = uploadFromS3();
  const body = {
    source_file_type: "EMAIL",
    email_request: {
      email_listener_identifier: listener,
      email_identifier: identifier,
      filename: filename,
    },
  };
  const resp = await utils.authenticatedFetch(url, {
    body: JSON.stringify(body),
    method: "post",
    headers: {
      "Content-Type": "application/json",
    },
  });
  return await resp.json();
};

export const cleanOfficeFile = async (fileId) => {
  try {
    const url = cleanOfficeFileUrl(fileId);
    const resp = await utils.authenticatedFetch(url);
    return await resp.json();
  } catch (error) {
    logger.exception(error, {
      breadcrumb: { call: "cleanOfficeFileUrl", fileId },
    });
    throw error;
  }
};

export const getViewTypeFromMime = (mime) => {
  if (!mime) return null;
  if (mime.startsWith("image/")) return "raw";
  if (rawViewableMimeTypes.includes(mime)) return "raw";
  if (htmlViewableMimeTypes.includes(mime)) return "html";
  return null;
};

export const traverseFileSystemEntry = async (entry, path = "") => {
  const results = [];

  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => {
      entry.file(resolve, reject);
    });
    results.push({
      file,
      relativePath: path ? `${path}/${entry.name}` : entry.name,
    });
  } else if (entry.isDirectory) {
    const dirReader = entry.createReader();
    const readAllEntries = async () => {
      const allEntries = [];
      let entries;
      do {
        entries = await new Promise((resolve, reject) => {
          dirReader.readEntries(resolve, reject);
        });
        allEntries.push(...entries);
      } while (entries.length > 0);
      return allEntries;
    };

    const entries = await readAllEntries();
    const newPath = path ? `${path}/${entry.name}` : entry.name;

    if (entries.length === 0) {
      results.push({
        file: null,
        relativePath: newPath,
        isEmptyDirectory: true,
      });
    } else {
      for (const childEntry of entries) {
        const childResults = await traverseFileSystemEntry(childEntry, newPath);
        results.push(...childResults);
      }
    }
  }

  return results;
};

export const captureDroppedEntries = (items) => {
  const captured = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === "file") {
      const entry = item.webkitGetAsEntry?.();
      if (entry) {
        captured.push({ entry, file: null });
      } else {
        const file = item.getAsFile();
        if (file) {
          captured.push({ entry: null, file });
        }
      }
    }
  }
  return captured;
};

const IGNORED_FILES = [".DS_Store", "Thumbs.db", "desktop.ini", ".gitkeep"];
const shouldIgnoreFile = (filename) => {
  const name = filename.toLowerCase();
  return (
    IGNORED_FILES.some((ignored) => name === ignored.toLowerCase()) ||
    name.startsWith("._")
  );
};

export const processDroppedItems = async (captured) => {
  const results = [];
  for (const item of captured) {
    if (item.entry) {
      const entryResults = await traverseFileSystemEntry(item.entry, "");
      results.push(...entryResults);
    } else if (item.file) {
      results.push({ file: item.file, relativePath: item.file.name });
    }
  }
  return results.filter(
    (item) => item.isEmptyDirectory || !shouldIgnoreFile(item.file.name)
  );
};

export const extractDirectoryPaths = (filesWithPaths) => {
  const dirPaths = new Set();
  for (const { relativePath } of filesWithPaths) {
    const parts = relativePath.split("/");
    parts.pop();
    let currentPath = "";
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      dirPaths.add(currentPath);
    }
  }
  return Array.from(dirPaths).sort(
    (a, b) => a.split("/").length - b.split("/").length
  );
};

// Fetches a file as a blob and triggers a named browser download.
// Uses a blob URL so the browser respects the filename regardless of the S3 key name.
export const triggerBlobDownload = async (url, filename) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Download failed: ${response.status} ${response.statusText}`
    );
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "download";
  a.click();
  URL.revokeObjectURL(blobUrl);
};

// Normalize a filename for tolerant matching. AI-recorded source filenames are
// plain text and may differ from the registered file name in apostrophe/quote
// style, whitespace, or case — so compare on a normalized form.
export const normalizeFilename = (name) =>
  (name || "")
    .normalize("NFKC")
    .replace(/[‘’ʼ′]/g, "'")
    .replace(/[“”″]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// Strip a leading numbering prefix ("1. ", "01 - ", "2) ") that the AI may add
// or drop relative to the registered file name.
const stripLeadingIndex = (name) =>
  normalizeFilename((name || "").replace(/^\s*\d+\s*[.)-]+\s*/, ""));

// Find a file entry ({ file_name, file_id }) by name, tolerant to quote/space/
// case and leading-index differences. Returns the entry or null.
export const matchFileByName = (files, filename) => {
  if (!filename || !files?.length) return null;
  const target = normalizeFilename(filename);
  const exact = files.find((f) => normalizeFilename(f.file_name) === target);
  if (exact) return exact;
  const stripped = stripLeadingIndex(filename);
  return files.find((f) => stripLeadingIndex(f.file_name) === stripped) || null;
};
